import React, { useMemo } from 'react';
import { Grid, Button, makeStyles, styled } from '@material-ui/core';
import { Email, BookmarkBorderOutlined, Bookmark } from '@material-ui/icons';
import { useIntl } from 'react-intl';

import { SdmRichText, SdmStack } from '..';
import translations from 'utils/translations';

import { Contents, Row, Container, Divider } from './Contents';
import { PageGeneratorThemeProvider, Typography, theme } from './mui-overrides';
import { Caption } from 'components/Caption';
import { noop } from 'utils/noop';
import { theme as MainTheme } from 'theme';
const useStyles = makeStyles(() => ({
  actions: {
    marginBottom: 20,
    '@media (min-width: 960px)': {
      minHeight: 78,
    },
  },
  overview: {
    ...theme.typography.overview,
    '& div[data-contents=true] > div[data-block=true] > div[data-offset-key]': {
      padding: 0,
      marginBottom: 10,
    },
    '& div[data-contents=true] > div[data-block=true]:last-of-type > div[data-offset-key]':
      {
        marginBottom: 0,
      },
    '& a': {
      color: MainTheme.colors.primary.red,
    },
  },
  captionContainer: {
    '@media screen and (min-width: 992px)': {
      marginTop: 45,
    },
    marginTop: 28,
  },
  cover: {
    ['@media (min-width: 992px)']: {
      marginBottom: 70,
    },
    marginBottom: 40,
  },
  visitBrandPage: {
    textDecoration: 'underline',
    '@media screen and (min-width: 992px)': {
      fontSize: 16,
      lineHeight: '18px',
    },
    fontSize: 12,
    lineHeight: '13px',
    fontWeight: 300,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const Image = styled('img')({
  maxWidth: '100%',
  height: 'auto',
});

export const ProductPageTemplate = ({
  name,
  brandName,
  cover,
  heroText,
  overview,
  contents = [],
  onContactUs = noop,
  onBookmarks = noop,
  bookmarks,
  onVisitBrandPage = noop,
  category = undefined,
  collection = undefined,
}) => {
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const classes = useStyles();

  return (
    <PageGeneratorThemeProvider>
      <Container>
        <SdmStack>
          <Typography variant="subtitle">{`${brandName} ${i18n.introduces}`}</Typography>
          <Typography variant="title">{name}</Typography>
          {collection && collection?.length ? (
            <Typography
              variant="categoryPreview"
              style={{ marginBottom: 20 }}
            >{`${i18n.collectionPreview}: ${collection}`}</Typography>
          ) : null}
          {category ? (
            <>
              <div
                style={{
                  width: '83%',
                  height: 1,
                  backgroundColor: 'black',
                  margin: '0 auto',
                }}
              />
              <Typography variant="categoryPreview">{`${i18n.categoryPreview}: ${category}`}</Typography>
              <div
                style={{
                  width: '83%',
                  height: 1,
                  backgroundColor: 'black',
                  margin: '0 auto 20px',
                }}
              />
            </>
          ) : null}
          <Grid
            item
            xs={12}
            className={classes.actions}
            container
            justify="center"
          >
            <Grid item container xs={12} md={8}>
              <Grid container item md xs={12} justify="center">
                <Button
                  color="primary"
                  variant="text"
                  onClick={() => onVisitBrandPage()}
                  className={classes.visitBrandPage}
                >
                  {i18n.visitBrandPage}
                </Button>
              </Grid>
              <Grid container item md xs={12} justify="center">
                <Button
                  color="primary"
                  variant="text"
                  onClick={() => onContactUs()}
                  startIcon={<Email />}
                >
                  {i18n.contactUs}
                </Button>
              </Grid>
              <Grid container item md xs={12} justify="center">
                <Button
                  color="primary"
                  variant="text"
                  startIcon={
                    bookmarks ? <Bookmark /> : <BookmarkBorderOutlined />
                  }
                  onClick={() => onBookmarks()}
                >
                  {bookmarks ? i18n.removeFromBookmarks : i18n.addToBookmarks}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Row
            itemProps={{
              md: 10,
            }}
          >
            <Image src={cover.img} alt={cover.alt} />
            {heroText && (
              <div className={classes.captionContainer}>
                <Caption text={heroText} textAlign="center" variant="cover" />
              </div>
            )}
          </Row>
          <Row className={classes.captionContainer}>
            <SdmRichText
              editorClassName={classes.overview}
              variant="white"
              editorStyleOverride={{
                maxHeight: 'none',
                marginRight: 0,
                paddingLeft: 0,
                minHeight: 'none !important',
              }}
              formControlStyle={{
                margin: 0,
              }}
              labelStyle={{
                display: 'none',
              }}
              value={overview}
              readOnly
            />
          </Row>
          <Row>
            <Divider />
          </Row>
        </SdmStack>
      </Container>
      <SdmStack>
        <Contents contents={contents} />
      </SdmStack>
    </PageGeneratorThemeProvider>
  );
};

export function useProductDefaultValue() {
  return useMemo(() => {
    return {
      name: 'Product',
      brandName: 'Brand',
      cover: {
        img: 'https://via.placeholder.com/1200x900',
        alt: '1200x900',
      },
      overview: {
        blocks: [
          {
            key: (Math.random() * 10000).toFixed(0),
            text: '',
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {},
          },
        ],
        entityMap: {},
      },
    };
  }, []);
}
